const BT_CONTENT_ROOT = "bt-content-root";

export function addCloseOnScroll(
  triggerNode: Element,
  onScrollCallback: (event: Event) => void,
  useDocument?: boolean,
): void {
  const closestScrollableContainer = getClosestScrollContainer(triggerNode);
  const closestGrid = getClosestReactGrid(triggerNode);

  if (closestScrollableContainer) {
    closestScrollableContainer.addEventListener("scroll", onScrollCallback, {
      passive: true,
    });
  }

  if (closestGrid) {
    closestGrid.addEventListener("scroll", onScrollCallback, { passive: true });
  }

  if (useDocument) {
    document.addEventListener("scroll", onScrollCallback, { passive: true });
  }
}

export function removeCloseOnScroll(
  triggerNode: Element,
  onScrollCallback: (event: Event) => void,
  useDocument?: boolean,
): void {
  const bodyEl = document.body;
  const closestScrollableContainer =
    getClosestScrollContainer(triggerNode) ?? bodyEl;
  const closestGrid = getClosestReactGrid(triggerNode) ?? bodyEl;
  if (closestScrollableContainer !== bodyEl) {
    closestScrollableContainer.removeEventListener("scroll", onScrollCallback);
  }

  if (closestGrid !== bodyEl) {
    closestGrid.removeEventListener("scroll", onScrollCallback);
  }

  if (useDocument) {
    document.removeEventListener("scroll", onScrollCallback);
  }
}

function getClosestScrollParent(node: Element | null): Element | null {
  if (node === null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getClosestScrollParent(node.parentElement);
  }
}

function getClosestScrollContainer(
  triggerNode: Element,
): HTMLElement | undefined {
  const insideBdsLayout =
    triggerNode.closest(".bds-layout") !== null ||
    triggerNode.closest(".bds-section") !== null;
  const closestScrollParent = getClosestScrollParent(triggerNode);
  if (insideBdsLayout && closestScrollParent !== null) {
    return closestScrollParent as HTMLElement;
  }
  const closestHeader = triggerNode.closest(".ant-layout-header");
  if (closestHeader !== null) {
    return closestHeader as HTMLElement;
  }

  const closestBtModalLayout = triggerNode.closest(".ModalContentContainer");
  if (closestBtModalLayout !== null) {
    return closestBtModalLayout as HTMLElement;
  }

  return getClosestReactModal(triggerNode, "body");
}

export function getClosestModal(
  triggerNode?: Element | null,
  section: "content" | "body" = "content",
): HTMLElement {
  const closestReactModal = getClosestReactModal(triggerNode, section);
  return closestReactModal ?? document.body;
}

export function getClosestReactGrid(
  triggerNode?: Element,
): HTMLElement | undefined {
  if (triggerNode) {
    const closestKendoGrid = triggerNode.closest(
      ".k-grid-content.k-virtual-content",
    );
    if (closestKendoGrid !== null) {
      return closestKendoGrid as HTMLElement;
    }

    const closestAntGrid = triggerNode.closest(".ant-table-body");
    if (closestAntGrid !== null) {
      return closestAntGrid as HTMLElement;
    }
  }

  return undefined;
}

export function getClosestReactModal(
  triggerNode?: Element | null,
  section: "content" | "body" = "content",
): HTMLElement | undefined {
  if (triggerNode) {
    const closestHeader = triggerNode.closest(".ant-layout-header");
    if (closestHeader !== null) {
      return closestHeader as HTMLElement;
    }

    const closestModal = triggerNode.closest(`.ant-modal-${section}`);
    if (closestModal !== null) {
      return closestModal as HTMLElement;
    }

    const closestDrawer = triggerNode.closest(`.ant-drawer-${section}`);
    if (closestDrawer !== null) {
      // Bottom sheets are anchored to the viewport bottom and tend to be short — keeping
      // popups inside the sheet would clip them or force tiny widths. Let them portal to body
      // so they can overflow the sheet naturally.
      const isBottomSheet = closestDrawer.closest(".bt-bottom-sheet") !== null;
      if (!isBottomSheet) {
        return closestDrawer as HTMLElement;
      }
    }

    const closestPopover = triggerNode.closest(`.ant-popover-${section}`);
    if (closestPopover !== null) {
      return closestPopover as HTMLElement;
    }

    const closestFullscreenElement = triggerNode.closest(`.Fullscreen`);
    if (closestFullscreenElement !== null) {
      return closestFullscreenElement as HTMLElement;
    }

    const closestZIndexContentRoot = triggerNode.closest(`.${BT_CONTENT_ROOT}`);
    if (closestZIndexContentRoot !== null) {
      return closestZIndexContentRoot as HTMLElement;
    }
  }

  return undefined;
}
